import React from 'react'
import {
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import DashboardCard from '@/components/dashboard/DashboardCard'
import CompetitorBadge from '@/components/market/CompetitorBadge'
import { formatMoney } from '@/lib/format'
import { MARKET_COLORS } from '@/lib/market'
import type { MarketPriceDomain, MarketValueMap, MarketValuePoint } from '@/types/market'

/**
 * Price and reviews: each hotel's typical list price against its guest review score. The
 * conclusion is the server's sentence; the chart is the evidence. Competitors are their numbered
 * discs (the same numbers as everywhere else), you are teal. Points missing either value cannot be
 * placed and are named under the chart with the reason.
 */

type Plotted = MarketValuePoint & { reviewScore: number; typicalPrice: number; number: number | null }

/** Review scores read with one decimal, as Booking.com prints them ("7.0", never "7"). */
export function scoreLabel(score: number): string {
  return Number.isInteger(score) ? `${score}.0` : String(score)
}

function PointShape(props: { cx?: number; cy?: number; payload?: Plotted }) {
  const { cx, cy, payload } = props
  if (cx == null || cy == null || !payload) return <g />
  if (payload.isYou) {
    return (
      <g>
        <circle cx={cx} cy={cy} r={13} fill={MARKET_COLORS.you} fillOpacity={0.15} />
        <circle cx={cx} cy={cy} r={10} fill={MARKET_COLORS.you} stroke="#ffffff" strokeWidth={2} />
        <text x={cx} y={cy + 3} textAnchor="middle" fontSize={8} fontWeight={700} fill="#ffffff" letterSpacing={0.4}>
          YOU
        </text>
      </g>
    )
  }
  return (
    <g>
      <circle cx={cx} cy={cy} r={9} fill={MARKET_COLORS.competitor} stroke="#ffffff" strokeWidth={2} />
      <text x={cx} y={cy + 3.5} textAnchor="middle" fontSize={10} fontWeight={600} fill="#ffffff">
        {payload.number ?? ''}
      </text>
    </g>
  )
}

function ValueTooltip({
  active,
  payload,
  currency,
}: {
  active?: boolean
  payload?: { payload: Plotted }[]
  currency: string
}) {
  if (!active || !payload?.length) return null
  const p = payload[0].payload
  return (
    <div className="rounded-lg border border-border/60 bg-white px-3 py-2 text-xs shadow-lg shadow-black/5">
      <p className="max-w-[220px] truncate font-semibold text-foreground">{p.name}</p>
      <p className="mt-1 tabular-nums text-muted-foreground">
        Review score <span className="font-medium text-foreground">{scoreLabel(p.reviewScore)}</span>
      </p>
      <p className="tabular-nums text-muted-foreground">
        Typical price <span className="font-medium text-foreground">{formatMoney(p.typicalPrice, currency)}</span>
      </p>
    </div>
  )
}

export default function ValueMap({
  valueMap,
  numbers,
  currency,
  domain,
  reducedMotion = false,
}: {
  valueMap: MarketValueMap
  numbers: Map<number, number>
  currency: string
  domain: MarketPriceDomain | null
  reducedMotion?: boolean
}) {
  const plotted: Plotted[] = []
  const missing: { name: string; reason: string }[] = []
  for (const point of valueMap.points) {
    if (point.reviewScore == null) {
      missing.push({ name: point.isYou ? 'You' : point.name, reason: 'no review score' })
    } else if (point.typicalPrice == null) {
      missing.push({ name: point.isYou ? 'You' : point.name, reason: 'no typical price' })
    } else {
      plotted.push({
        ...point,
        reviewScore: point.reviewScore,
        typicalPrice: point.typicalPrice,
        number: point.marketPropertyId == null ? null : (numbers.get(point.marketPropertyId) ?? null),
      })
    }
  }
  const you = plotted.find((p) => p.isYou)
  const others = plotted.filter((p) => !p.isYou)

  return (
    <DashboardCard title="Price and reviews" description={valueMap.sentence ?? undefined}>
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_260px]">
        <div
          className="relative h-[260px] sm:h-[300px]"
          role="img"
          aria-label={valueMap.sentence ?? 'Typical price against review score'}
        >
          <span aria-hidden="true" className="absolute left-0 top-0 text-[11px] text-muted-foreground">
            Typical price
          </span>
          {plotted.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 600, height: 300 }}>
              <ScatterChart margin={{ top: 24, right: 16, bottom: 20, left: 0 }} accessibilityLayer={false}>
                <CartesianGrid stroke={MARKET_COLORS.grid} />
                <XAxis
                  type="number"
                  dataKey="reviewScore"
                  name="Review score"
                  domain={[(min: number) => Math.max(0, Math.floor(min - 0.5)), 10]}
                  allowDecimals={false}
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: '#64748b' }}
                  label={{
                    value: 'Review score',
                    position: 'insideBottom',
                    offset: -12,
                    fontSize: 11,
                    fill: '#64748b',
                  }}
                />
                <YAxis
                  type="number"
                  dataKey="typicalPrice"
                  name="Typical price"
                  width={64}
                  domain={domain ? [domain.min, domain.max] : ['auto', 'auto']}
                  ticks={domain?.ticks}
                  allowDataOverflow
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: '#64748b', style: { fontVariantNumeric: 'tabular-nums' } }}
                  tickFormatter={(v: number) => formatMoney(v, currency)}
                />
                {you && (
                  <ReferenceLine
                    x={you.reviewScore}
                    stroke={MARKET_COLORS.you}
                    strokeOpacity={0.35}
                    strokeDasharray="3 3"
                  />
                )}
                {you && (
                  <ReferenceLine
                    y={you.typicalPrice}
                    stroke={MARKET_COLORS.you}
                    strokeOpacity={0.35}
                    strokeDasharray="3 3"
                  />
                )}
                <Tooltip
                  cursor={false}
                  isAnimationActive={false}
                  content={(props) => (
                    <ValueTooltip
                      active={props.active}
                      payload={props.payload as unknown as { payload: Plotted }[] | undefined}
                      currency={currency}
                    />
                  )}
                />
                <Scatter data={others} shape={PointShape} isAnimationActive={!reducedMotion} legendType="none" />
                {you && (
                  <Scatter data={[you]} shape={PointShape} isAnimationActive={!reducedMotion} legendType="none" />
                )}
              </ScatterChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-full items-center justify-center rounded-lg border border-dashed border-border text-xs text-muted-foreground">
              No hotel has both a review score and a typical price yet.
            </div>
          )}
        </div>

        <div className="space-y-3">
          <ul className="space-y-1.5" aria-label="Hotels on the map">
            {plotted
              .slice()
              .sort((a, b) => Number(b.isYou) - Number(a.isYou))
              .map((p) => (
                <li key={p.id} className="flex items-center gap-2 text-xs">
                  {p.isYou ? <CompetitorBadge isYou /> : <CompetitorBadge number={p.number} />}
                  <span className="min-w-0 flex-1 truncate text-foreground" title={p.name}>
                    {p.name}
                  </span>
                  <span className="shrink-0 tabular-nums text-muted-foreground">
                    {scoreLabel(p.reviewScore)} · {formatMoney(p.typicalPrice, currency)}
                  </span>
                </li>
              ))}
          </ul>
          {missing.length > 0 && (
            <ul
              className="space-y-1 border-t border-border/40 pt-3 text-[11px] text-muted-foreground"
              data-testid="value-map-missing"
            >
              {missing.map((m) => (
                <li key={m.name}>
                  Not shown: {m.name} ({m.reason})
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </DashboardCard>
  )
}
